import React from 'react';
import { useRealtimeCurrency } from '../../hooks/useRealtimeCurrency';
import { RefreshCw, Zap } from 'lucide-react';

interface CurrencySyncSelectorProps {
  compact?: boolean;
  showExchangeRate?: boolean;
  className?: string;
}

export const CurrencySyncSelector: React.FC<CurrencySyncSelectorProps> = ({
  compact = false,
  showExchangeRate = true,
  className = ''
}) => {
  const {
    activeCode,
    supportedCurrencies,
    switchCurrency,
    refreshRates,
    isRefreshing
  } = useRealtimeCurrency();

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Currency Selector */}
      <div className="relative inline-flex items-center">
        <select
          value={activeCode}
          onChange={(e) => switchCurrency(e.target.value, true)}
          className={`appearance-none bg-zinc-800 hover:bg-zinc-700/90 text-white font-bold rounded-md border border-zinc-700 focus:ring-2 focus:ring-emerald-400 outline-none cursor-pointer transition-all ${
            compact ? 'py-1 pl-2 pr-7 text-xs' : 'py-1.5 pl-3 pr-8 text-xs'
          }`}
          title="Select display currency (Auto-converts prices in real-time)"
        >
          {supportedCurrencies.map((curr) => (
            <option key={curr.code} value={curr.code} className="bg-zinc-900 text-white">
              {curr.flag} {curr.code} ({curr.symbol}) - {curr.name}
            </option>
          ))}
        </select>
        <span className="pointer-events-none absolute right-2 text-zinc-400 text-[10px]">▼</span>
      </div>

      {/* Real-time sync badge & refresh button */}
      {showExchangeRate && (
        <div className="flex items-center gap-1.5 bg-zinc-800/80 px-2 py-1 rounded-md border border-zinc-700/80 text-[11px] text-zinc-300">
          <span className="flex items-center gap-1 font-mono text-emerald-400">
            <Zap className="w-3 h-3 text-emerald-400 fill-emerald-400" />
            <span className="hidden sm:inline">Live Auto-Sync</span>
          </span>

          <button
            type="button"
            onClick={refreshRates}
            disabled={isRefreshing}
            className="text-zinc-400 hover:text-white p-0.5 rounded transition-colors cursor-pointer disabled:opacity-50"
            title="Force refresh live exchange rates"
          >
            <RefreshCw className={`w-3 h-3 ${isRefreshing ? 'animate-spin text-emerald-400' : ''}`} />
          </button>
        </div>
      )}
    </div>
  );
};
